<template>
  <div class="app-container device-activation-page">
    <div class="global-header-wrap">
      <h3>{{ $t("router.deviceActivation") }}</h3>
    </div>

    <div class="device-activation-main">
      <!-- 顶部筛选 -->
      <el-card class="filter-card" :body-style="{ padding: '12px 16px' }">
        <div class="filter-row">
          <el-select
            v-model="params.deviceModel"
            placeholder="全部型号"
            clearable
            size="small"
            class="filter-item"
          >
            <el-option label="全部型号" value="" />
            <el-option
              v-for="item in modelOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>

          <el-date-picker
            v-model="dateTimeRange"
            type="datetimerange"
            size="small"
            class="filter-item filter-datetime"
            range-separator="至"
            start-placeholder="开始日期时间"
            end-placeholder="结束日期时间"
            value-format="yyyy-MM-dd HH:mm:ss"
            :default-time="['00:00:00', '23:59:59']"
            @change="onDateTimeChange"
          />

          <el-select
            v-model="params.status"
            placeholder="全部状态"
            clearable
            size="small"
            class="filter-item"
          >
            <el-option label="全部状态" value="" />
            <el-option label="正常激活" value="success" />
            <el-option label="异常激活" value="region_warning" />
          </el-select>

          <el-button type="primary" size="small" @click="applyFilter">
            筛选
          </el-button>
        </div>
      </el-card>

      <!-- 中间汇总统计 -->
      <div class="summary-row" v-loading="statsLoading">
        <el-card shadow="never" class="summary-card">
          <div class="summary-title">
            累计激活总数（{{ modelDisplayName }}）
          </div>
          <div class="summary-value">{{ formatCount(stats.totalCount) }}</div>
        </el-card>
        <el-card shadow="never" class="summary-card">
          <div class="summary-title">
            累计异常激活总数（{{ modelDisplayName }}）
          </div>
          <div class="summary-value is-danger">
            {{ formatCount(stats.totalWarningCount) }}
          </div>
        </el-card>
        <el-card shadow="never" class="summary-card">
          <div class="summary-title">
            筛选时段激活总数（{{ modelDisplayName }}）
          </div>
          <div class="summary-value">{{ formatCount(stats.periodCount) }}</div>
        </el-card>
        <el-card shadow="never" class="summary-card">
          <div class="summary-title">
            筛选时段异常激活总数（{{ modelDisplayName }}）
          </div>
          <div class="summary-value is-danger">
            {{ formatCount(stats.periodWarningCount) }}
          </div>
        </el-card>
      </div>

      <!-- 明细表格 -->
      <el-card class="app-table-container" :body-style="{ padding: '10px' }">
        <div slot="header" class="table-section-header">
          <span class="table-section-title">当前筛选结果下激活数据明细</span>
          <el-button
            type="primary"
            size="small"
            :loading="exporting"
            @click="handleExport"
          >
            导出为Excel
          </el-button>
        </div>
        <div class="global-body-wrap" v-loading="loading">
          <TableView
            :tableData="tableData"
            :tableProp="tableProp"
            :currentPage="params.page"
            :pageSize="params.pageSize"
          />
        </div>
        <Pagination
          :pageSize="params.pageSize"
          :total="total"
          @pageNum="pageNum"
        />
      </el-card>
    </div>
  </div>
</template>

<script>
import TableView from "@/components/tableView";
import Pagination from "@/components/pagination";

export default {
  components: {
    TableView,
    Pagination,
  },
  computed: {
    tableProp() {
      return [
        { prop: "activationTime", label: "激活时间" },
        { prop: "serialNumber", label: "SN码" },
        { prop: "deviceModel", label: "型号" },
        { prop: "systemVersion", label: "版本信息" },
        { prop: "ip", label: "IP地址" },
        { prop: "status", label: "激活状态" },
      ];
    },
    modelDisplayName() {
      return this.params.deviceModel || "全部型号";
    },
  },
  data() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const y = yesterday.getFullYear();
    const m = String(yesterday.getMonth() + 1).padStart(2, "0");
    const d = String(yesterday.getDate()).padStart(2, "0");
    const defaultStart = `${y}-${m}-${d} 00:00:00`;
    const defaultEnd = `${y}-${m}-${d} 23:59:59`;
    return {
      loading: false,
      statsLoading: false,
      exporting: false,
      tableData: [],
      total: 0,
      params: {
        pageSize: 20,
        page: 1,
        deviceModel: "",
        status: "",
        startDate: defaultStart,
        endDate: defaultEnd,
      },
      dateTimeRange: [defaultStart, defaultEnd],
      modelOptions: [],
      stats: {
        totalCount: null,
        totalWarningCount: null,
        periodCount: null,
        periodWarningCount: null,
      },
    };
  },
  methods: {
    formatCount(val) {
      if (val === null || val === undefined || val === "") {
        return "-";
      }
      return val;
    },

    getErrorMsg(e, fallback) {
      return (
        (e.response && e.response.data && e.response.data.message) ||
        e.message ||
        fallback
      );
    },

    onDateTimeChange(val) {
      if (val && val.length === 2) {
        this.params.startDate = val[0];
        this.params.endDate = val[1];
      } else {
        this.params.startDate = "";
        this.params.endDate = "";
      }
    },

    baseParamsForApi() {
      const { deviceModel, status, startDate, endDate } = this.params;
      const p = {
        startDate,
        endDate,
        status: status || undefined,
      };
      if (deviceModel) {
        p.deviceModel = deviceModel;
      }
      return p;
    },

    filterParamsForApi() {
      return {
        page: this.params.page,
        pageSize: this.params.pageSize,
        ...this.baseParamsForApi(),
      };
    },

    async getStats() {
      this.statsLoading = true;
      try {
        const res = await this.$http.getDeviceActivationStats(
          this.baseParamsForApi()
        );
        const d = res.data || {};
        this.stats = {
          totalCount: d.totalCount,
          totalWarningCount: d.totalWarningCount,
          periodCount: d.periodCount,
          periodWarningCount: d.periodWarningCount,
        };
      } catch (e) {
        this.elMsg(this.getErrorMsg(e, "加载统计失败"), "error");
        this.stats = {
          totalCount: null,
          totalWarningCount: null,
          periodCount: null,
          periodWarningCount: null,
        };
      } finally {
        this.statsLoading = false;
      }
    },

    async getList() {
      this.loading = true;
      try {
        let res = await this.$http.getDeviceActivationList(
          this.filterParamsForApi()
        );
        const d = res.data || {};
        const rows = d.results || [];
        this.tableData = rows.map((row) => this.normalizeRow(row));
        this.total = d.count || 0;
      } catch (e) {
        this.elMsg(this.getErrorMsg(e, "加载列表失败"), "error");
      } finally {
        this.loading = false;
      }
    },

    normalizeRow(row) {
      const raw = row.status;
      let statusText = raw;
      if (raw === "success") {
        statusText = "正常激活";
      } else if (raw === "region_warning") {
        statusText = "异常激活";
      }
      return {
        ...row,
        status: statusText,
      };
    },

    async getModelOptions() {
      try {
        let res = await this.$http.getActivationModelList();
        this.modelOptions = (res.data.items || []).map((name) => ({
          label: name,
          value: name,
        }));
      } catch (e) {
        this.elMsg(this.getErrorMsg(e, "加载型号列表失败"), "error");
      }
    },

    applyFilter() {
      if (!this.params.startDate || !this.params.endDate) {
        this.elMsg("请选择时间范围", "warning");
        return;
      }
      this.params.page = 1;
      this.getStats();
      this.getList();
    },

    pageNum(page) {
      this.params.page = page;
      this.getList();
    },

    async handleExport() {
      this.exporting = true;
      try {
        let res = await this.$http.exportDeviceActivation(
          this.baseParamsForApi()
        );
        const blob = new Blob([res], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `设备激活明细_${new Date().toLocaleDateString()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        this.elMsg(this.getErrorMsg(e, "导出失败"), "error");
      } finally {
        this.exporting = false;
      }
    },
  },
  mounted() {
    this.getStats();
    this.getList();
    this.getModelOptions();
  },
};
</script>

<style lang="scss" scoped>
.device-activation-page {
  .device-activation-main {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 16px 20px 20px;
    gap: 16px;
    overflow: auto;
  }

  .app-table-container {
    margin: 0 !important;
    flex: 1;
    min-height: 0;
  }

  .filter-card {
    flex-shrink: 0;
  }

  .filter-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;

    ::v-deep .el-select .el-input__inner {
      height: 32px;
      line-height: 32px;
    }

    ::v-deep .el-date-editor.el-input__inner {
      height: 32px;
      line-height: 32px;
    }

    ::v-deep .el-range-editor.el-input__inner {
      display: inline-flex;
      align-items: center;
    }

    ::v-deep .el-button--small {
      padding: 8px 15px;
    }
  }

  .filter-item {
    min-width: 140px;
  }

  .filter-datetime {
    min-width: 360px;
  }

  .summary-row {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 16px;
    flex-shrink: 0;
  }

  @media (max-width: 1200px) {
    .summary-row {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  @media (max-width: 600px) {
    .summary-row {
      grid-template-columns: 1fr;
    }
  }

  .summary-card {
    border: 1px solid #ebeef5;

    .summary-title {
      font-size: 13px;
      color: #606266;
      margin-bottom: 12px;
      line-height: 1.4;
    }

    .summary-value {
      font-size: 28px;
      font-weight: 600;
      color: #303133;
      line-height: 1.2;

      &.is-danger {
        color: #f56c6c;
      }
    }
  }

  .table-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    min-height: 32px;
  }

  .table-section-title {
    font-size: 15px;
    font-weight: 500;
    color: #303133;
    line-height: 32px;
  }

  ::v-deep .app-table-container .el-card__header {
    padding: 12px 16px;
    border-bottom: 1px solid #ebeef5;
  }
}
</style>
